// Pré-visualização de um documento numa gaveta. Aba "Texto": o documento em trechos, com os usados na resposta do chat grifados em laranja.
// Aba "Arquivo": o original (PDF no leitor do navegador, imagem como imagem), só para uploads que o servidor guardou.
// Os trechos citados são navegáveis (anterior/próximo) e o resto do arquivo pode ser lido e pesquisado: é a forma de conferir a fonte.
// Dados: GET /api/client/documents/:id/content (mesma autorização do documento: próprio ou compartilhado, senão 404). Texto sempre como texto.
import { Fragment, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router';
import { ChevronDown, ChevronUp, Download, ExternalLink, FileText, Search, X } from 'lucide-react';
import { api, apiGetBlob, ApiError, type DocContent } from '../api';
import '../styles/doc-viewer.css';

/** `tab`: o que abre primeiro. Sem ele, o arquivo original (se houver) ou o texto. O chat pede 'text' (é lá que os trechos usados ficam grifados). */
export type ViewerTarget = { documentId: string; name: string; cited: number[]; focus: number | null; tab?: 'text' | 'file' };

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Texto do trecho com as ocorrências da busca marcadas. */
function Marked({ text, re }: { text: string; re: RegExp | null }) {
  if (!re) return <>{text}</>;
  const parts = text.split(re);   // `re` tem grupo de captura: os índices ímpares são as ocorrências
  return <>{parts.map((p, i) => (i % 2 ? <mark key={i} className="dv-hit">{p}</mark> : <Fragment key={i}>{p}</Fragment>))}</>;
}

export function DocViewer({ target, onClose }: { target: ViewerTarget; onClose: () => void }) {
  const [doc, setDoc] = useState<DocContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [hit, setHit] = useState(0);
  const [hitCount, setHitCount] = useState(0);
  const [at, setAt] = useState(0);   // posição entre os trechos citados
  const [tab, setTab] = useState<'text' | 'file' | null>(target.tab ?? null);
  const [file, setFile] = useState<{ url: string; mime: string } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const onDocs = useLocation().pathname.startsWith('/documentos');
  const bodyRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const opener = useRef<Element | null>(document.activeElement);

  const cited = useMemo(() => [...new Set(target.cited)].sort((a, b) => a - b), [target.cited]);
  const citedSet = useMemo(() => new Set(cited), [cited]);

  useEffect(() => {
    let alive = true;
    setDoc(null); setError(null);
    api<DocContent>(`/api/client/documents/${target.documentId}/content`)
      .then((d) => { if (alive) setDoc(d); })
      .catch((e) => { if (alive) setError(e instanceof ApiError && e.status === 404 ? 'Este documento não está mais disponível (foi apagado ou deixou de ser compartilhado com você).' : (e as Error).message); });
    return () => { alive = false; };
  }, [target.documentId]);

  const textless = doc != null && doc.chunks.length === 0;   // imagem: só o arquivo
  const view: 'text' | 'file' = textless ? 'file' : tab ?? (doc?.hasFile ? 'file' : 'text');
  const tabs = doc != null && doc.hasFile && !textless;

  // o original só é baixado quando a aba "Arquivo" é aberta; o endereço temporário é liberado ao sair
  useEffect(() => {
    if (!doc || view !== 'file' || !doc.hasFile || file) return;
    let alive = true, url: string | null = null;
    apiGetBlob(`/api/client/documents/${target.documentId}/file`)
      .then((b) => { url = URL.createObjectURL(b); if (alive) setFile({ url, mime: b.type || doc.mime }); else URL.revokeObjectURL(url); })
      .catch((e) => { if (alive) setFileError((e as Error).message); });
    return () => { alive = false; };
  }, [doc, view]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (file) URL.revokeObjectURL(file.url); }, [file]);

  const goto = (seq: number, smooth = false) => {
    const el = bodyRef.current?.querySelector<HTMLElement>(`[data-seq="${seq}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
  };

  // ao abrir (e quando o texto chega) vai para o trecho clicado
  useEffect(() => {
    if (!doc || view !== 'text') return;
    const first = target.focus != null && citedSet.has(target.focus) ? target.focus : cited[0];
    if (first == null) return;
    setAt(Math.max(0, cited.indexOf(first)));
    goto(first);
  }, [doc, view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { panelRef.current?.focus(); const o = opener.current as HTMLElement | null; return () => { o?.focus?.(); }; }, []);

  const step = (d: number) => {
    if (!cited.length) return;
    const n = (at + d + cited.length) % cited.length;
    setAt(n); goto(cited[n], true);
  };

  // busca: só a partir de 2 caracteres; Enter vai para a próxima ocorrência
  const re = useMemo(() => (query.trim().length >= 2 ? new RegExp(`(${escapeRe(query.trim())})`, 'gi') : null), [query]);
  useEffect(() => {
    const marks = bodyRef.current?.querySelectorAll<HTMLElement>('.dv-hit') ?? [];
    setHitCount(marks.length);
    marks.forEach((m, i) => m.classList.toggle('on', i === hit));
    if (marks.length) marks[Math.min(hit, marks.length - 1)].scrollIntoView({ block: 'center' });
  }, [re, hit, doc, view]);
  const nextHit = (d: number) => { if (hitCount) setHit((h) => (h + d + hitCount) % hitCount); };

  const keys = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } };

  const isImage = (file?.mime ?? doc?.mime ?? '').startsWith('image/');
  const sub = !doc ? 'carregando…' : textless ? (doc.mime.startsWith('image/') ? 'imagem' : 'sem texto') : `${doc.chunkCount} ${doc.chunkCount === 1 ? 'trecho' : 'trechos'}`;

  return createPortal(
    <div className="dv-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <aside className={`dv${view === 'file' ? ' wide' : ''}`} ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={`Fonte: ${target.name}`} onKeyDown={keys}>
        <header className="dv-h">
          <FileText aria-hidden="true" />
          <div className="dv-title"><b title={target.name}>{target.name}</b><span>{sub}</span></div>
          {view === 'file' && file && <a className="btn sm ghost" href={file.url} download={target.name} title="Baixar o arquivo"><Download /> Baixar</a>}
          {!onDocs && <Link className="btn sm ghost" to="/documentos" onClick={onClose} title="Abrir a página de documentos"><ExternalLink /> Documentos</Link>}
          <button type="button" className="btn sm" onClick={onClose} aria-label="Fechar"><X /></button>
        </header>

        {tabs && (
          <div className="dv-tabs" role="tablist" aria-label="O que mostrar">
            <button type="button" role="tab" aria-selected={view === 'file'} className={view === 'file' ? 'on' : ''} onClick={() => setTab('file')}>Arquivo original</button>
            <button type="button" role="tab" aria-selected={view === 'text'} className={view === 'text' ? 'on' : ''} onClick={() => setTab('text')}>Texto extraído</button>
          </div>
        )}

        {view === 'text' && (
          <div className="dv-bar">
            {cited.length > 0 ? (
              <div className="dv-cited">
                <span className="dv-key" aria-hidden="true" />
                <span>{cited.length === 1 ? '1 trecho usado na resposta' : `${cited.length} trechos usados na resposta`}</span>
                {cited.length > 1 && <>
                  <span className="dv-pos">{at + 1}/{cited.length}</span>
                  <button type="button" className="btn sm" onClick={() => step(-1)} aria-label="Trecho anterior"><ChevronUp /></button>
                  <button type="button" className="btn sm" onClick={() => step(1)} aria-label="Próximo trecho"><ChevronDown /></button>
                </>}
              </div>
            ) : <div className="dv-cited dv-mute">{target.focus === null && !target.tab ? 'texto que o assistente lê deste arquivo' : 'os trechos usados aparecem grifados quando a resposta termina'}</div>}
            <div className="dv-search">
              <Search aria-hidden="true" />
              <input value={query} placeholder="Buscar no arquivo" aria-label="Buscar no arquivo" maxLength={80}
                onChange={(e) => { setQuery(e.target.value); setHit(0); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); nextHit(e.shiftKey ? -1 : 1); } }} />
              {re && <span className="dv-count">{hitCount ? `${Math.min(hit, hitCount - 1) + 1}/${hitCount}` : '0'}</span>}
            </div>
          </div>
        )}

        {view === 'file' ? (
          <div className="dv-file">
            {error ? <div className="dv-state">{error}</div>
              : fileError ? <div className="dv-state">{fileError}</div>
                : !file ? <div className="dv-state" role="status">abrindo o arquivo…</div>
                  : isImage ? <div className="dv-img"><img src={file.url} alt={target.name} /></div>
                    : <iframe className="dv-pdf" src={file.url} title={target.name} />}
          </div>
        ) : (
          <div className="dv-body" ref={bodyRef}>
            {error ? <div className="dv-state">{error}</div>
              : !doc ? <div className="dv-skel" aria-hidden="true">{[88, 100, 72, 94, 60].map((w, i) => <div key={i} className="skel" style={{ width: `${w}%` }} />)}</div>
                : <>
                  {!doc.hasFile && doc.mime === 'application/pdf' && <div className="dv-note">O PDF original não foi guardado (só uploads feitos depois desta versão). Reenvie o arquivo para abri-lo no leitor.</div>}
                  {doc.chunks.map((c) => {
                    const on = citedSet.has(c.seq);
                    return (
                      <section key={c.seq} data-seq={c.seq} className={`dv-chunk${on ? ' cited' : ''}`}>
                        {on && <span className="dv-tag">trecho {c.seq + 1}{c.section ? ` · ${c.section}` : ''}</span>}
                        <p><Marked text={c.text} re={re} /></p>
                      </section>
                    );
                  })}
                </>}
          </div>
        )}
      </aside>
    </div>,
    document.body,
  );
}
